import { useContext, useEffect, useState } from "react";
import moment from "moment-jalaali";
import FileDetails from "../components/dashboard/file-details";
import ServiceSlider from "../components/dashboard/service-slider";
import MessageBox from "../components/dashboard/message-box";
import Row from "../components/tailwind/row";
import AuthContext from "../components/auth-context";
import FileApi from "../api/file";
import PortApi from "../api/port";
import FileOpApi from "../api/file-op";
import AppLink from "../components/router/app-link";

export default function Dashboard() {
  const context = useContext(AuthContext);
  const { activeFileId } = context;

  const [activeFile, setActiveFile] = useState(null);
  const [portState, setPortState] = useState(null);
  const [loadingService, setLoadingService] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);

  useEffect(() => {
    if (!activeFileId) {
      setActiveFile(null);
      setPortState(null);
      setFileLoading(false);
      return;
    }

    let isMounted = true;
    setFileLoading(true);
    setActiveFile(null);
    setPortState(null);

    const fileRequest = new FileApi(context)
      .get(activeFileId)
      .then((data) => {
        if (!isMounted) return;
        setActiveFile(data);
        setLoadingService(false);
        context.setServiceType(data.fileMedium);
        context.setInternetCommands(data.commands);
      })
      .catch((error) => {
        if (!isMounted) return;
        console.error("FileApi error:", error);
        setActiveFile(null);
      });

    const portRequest = new PortApi(context)
      .get(activeFileId)
      .then((data) => {
        if (!isMounted) return;
        switch (data?.portState) {
          case 1:
            setPortState("up");
            break;
          case 2:
          case 3:
            setPortState("down");
            break;
          default:
            setPortState(null);
            break;
        }
      })
      .catch((error) => {
        if (!isMounted) return;
        console.error("PortApi error:", error);
        setPortState(null);
      });

    Promise.allSettled([fileRequest, portRequest]).finally(() => {
      if (isMounted) {
        setFileLoading(false);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [activeFileId]);

  const isNgnService = activeFile?.fileMedium === 40;

  const startDate = activeFile?.activeService?.startDateTime
    ? moment(activeFile.activeService.startDateTime)
    : null;

  const endDate = activeFile?.activeService?.endDateTime
    ? moment(activeFile.activeService.endDateTime)
    : null;

  const timeOfService =
    startDate && endDate
      ? {
          total: endDate.diff(startDate, "days"),
          remind: endDate.diff(moment(), "days"),
        }
      : {
          total: 0,
          remind: 0,
        };

  const hasNoRemindTraffic =
    Array.isArray(activeFile?.constraints) &&
    activeFile.constraints.length > 0 &&
    activeFile.constraints.every((constraint) => constraint.remind < 0);

  const handleActivateNextService = async () => {
    if (!activeFileId || loadingService) return;

    try {
      const { errorMessage } = await new FileOpApi(
        context
      ).terminateCurrentService(activeFileId);

      if (!errorMessage) {
        setLoadingService(true);
      } else {
        console.error("FileOpApi error:", errorMessage);
      }
    } catch (error) {
      console.error("FileOpApi error:", error);
    }
  };

  // 1. حالت لودینگ
  if (fileLoading) {
    return (
      <div className="flex min-h-[360px] w-full flex-col items-center justify-center rounded-2xl border border-gray-100 bg-white/70 p-8 shadow-sm backdrop-blur-sm">
        <div className="relative flex items-center justify-center">
          <div className="h-12 w-12 rounded-full border-4 border-blue-100"></div>
          <div className="absolute h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
        </div>
        <p className="mt-4 text-sm font-medium text-gray-600">
          در حال دریافت و بروزرسانی اطلاعات پرونده...
        </p>
      </div>
    );
  }

  if (!activeFileId) {
    return (
      <div className="flex min-h-[220px] w-full flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50/70 p-8 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400">
          <i className="fas fa-folder-open text-xl"></i>
        </div>
        <p className="mt-3 text-sm font-medium text-gray-600">
          لطفاً ابتدا یک پرونده را انتخاب کنید.
        </p>
      </div>
    );
  }

  // 3. حالت عدم وجود اطلاعات پرونده
  if (!activeFile?.title) {
    return (
      <div className="flex min-h-[220px] w-full flex-col items-center justify-center rounded-2xl border border-dashed border-amber-200 bg-amber-50/50 p-8 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-500">
          <i className="fas fa-exclamation-triangle text-xl"></i>
        </div>
        <p className="mt-3 text-sm font-medium text-gray-700">
          اطلاعاتی برای پرونده انتخاب‌شده یافت نشد.
        </p>
      </div>
    );
  }

  const showServiceSlider = activeFile.status?.code === 10;

  return (
    <div className="w-full space-y-5" dir="rtl">
      {activeFile?.message && (
        <Row>
          <div className="w-full">
            <MessageBox message={activeFile.message} />
          </div>
        </Row>
      )}

      <Row>
        <div className="grid w-full grid-cols-1 items-stretch gap-5 lg:grid-cols-2">
          
          <div className="flex flex-col">
            {showServiceSlider ? (
              <ServiceSlider
                timeOfService={timeOfService}
                constraints={activeFile?.constraints}
                isNgnService={isNgnService}
              />
            ) : (
              <div className="flex h-full min-h-[260px] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50/80 p-6 text-center transition-all">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400">
                  <i className="fas fa-wifi-slash text-xl"></i>
                </div>
                
                <span className="mt-3 text-sm font-medium text-gray-500">
                  سرویس فعالی برای نمایش وجود ندارد.
                </span>
            {activeFile.status.code === 11
            &&
            (  <AppLink
                  id="usageDetails"
                  to={isNgnService ? "/ngnUsages" : "/usages"}
                  className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-50 px-3.5 py-1.5 text-xs font-semibold text-blue-600 transition-colors hover:bg-blue-100 hover:text-blue-700"
                >
                  <i className="far fa-hand-point-left text-xs"></i>
                  <span>مشاهده جزئیات مصرف</span>
                </AppLink>)}
              </div>
            )}
          </div>

          <div className="flex flex-col">
            <FileDetails
              activeService={
                loadingService
                  ? {
                      ...(activeFile?.activeService ?? {}),
                      serviceTitle: "در حال عملیات...",
                    }
                  : activeFile?.activeService
              }
              preOrderedServiceTitle={
                loadingService ? "" : activeFile?.preOrderedServiceTitle
              }
              hasNoRemindTraffic={hasNoRemindTraffic}
              status={activeFile?.status}
              onActivateNextService={handleActivateNextService}
              link={portState}
              ipService={activeFile?.ipService}
            />
          </div>

        </div>
      </Row>
    </div>
  );
}
